package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/pagination")
@Composable
fun ComposePaginationPage() {
    BlogLayout(
        PageProperties(
            "Pagination",
            "Pagination in Jetpack Compose involves efficiently loading and displaying large datasets in a user interface by fetching data in smaller, manageable chunks rather than all at once."
        ),
        BlogProperties("Jetpack Compose", "Pagination")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            KotlinCode(
                code = """
                     object StringUtils {
                         fun formatReadingPercentage(currentPage: Int, totalPages: Int): Double {
                             if (totalPages <= 0) return 0.0
                             val percentage = (currentPage.toDouble() / totalPages.toDouble()) * 100.0
                             return percentage.coerceIn(0.0, 100.0)
                         }
                     }
                """.trimIndent()
            )

            SpanText(
                text = "Number Pagination 1,2,3 ..... 5,6,7 (Desktop, Table, Web)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Shahidzbi4213/WallpaperApp-Cmp.git
            KotlinCode(
                code = $$"""
                    //Number Pagination (Arrow Previous - Next, Enter Specific Number)
                    //https://github.com/Shahidzbi4213/WallpaperApp-Cmp.git
                    
                    <string name="desktop_previous_page">Previous page</string>
                    <string name="desktop_next_page">Next page</string>
                    <string name="desktop_page_of">Page %1$d of %2$d</string>
                        
                    val GlassBorder = Color.White.copy(alpha = 0.10f)
                    val Ink950 = Color(0xFF0B0B12)
                    val Ember = Color(0xFFFF5A2E) 
                    
                    val Flare = Color(0xFFFF8A1E)   
                    val Crimson = Color(0xFFFF2E5B)  
                    val EmberGradient = Brush.linearGradient(listOf(Flare, Crimson))
                    
                    val TextHi = Color(0xFFF4F3FC)
                    val TextMid = Color(0xFFADA9C6)
                    val TextLow = Color(0xFF726E8C)
                    
                    
                    @Immutable
                    data class PageState(
                        val items: List<Wallpaper> = emptyList(),
                        val page: Int = 1,
                        val totalPages: Int = 1,
                        val isLoading: Boolean = false,
                        val error: StringResource? = null
                    ) {
                        val isEmpty: Boolean get() = !isLoading && error == null && items.isEmpty()
                    }
                    
                    //UI
                    PaginationBar(
                        currentPage = state.page,
                        totalPages = state.totalPages,
                        enabled = !state.isLoading,
                        onPageSelected = onPageSelected
                    )
                    
                    /**
                     * Numbered pagination. Desktop users want to know where they are and be able to go back to
                     * exactly where they were -- infinite scroll gives you neither.
                     *
                     * Feeds run to hundreds of pages, so the bar shows a window around the current page with the
                     * first and last always reachable, plus a jump box (clicking through to page 900 is not a plan).
                     */
                    @Composable
                    fun PaginationBar(
                        currentPage: Int,
                        totalPages: Int,
                        onPageSelected: (Int) -> Unit,
                        modifier: Modifier = Modifier,
                        enabled: Boolean = true
                    ) {
                        if (totalPages <= 1) return

                        Row(
                            modifier = modifier
                                .fillMaxWidth()
                                // Opaque backdrop and a hairline: without them the grid scrolls up behind the bar
                                // and the two read as one surface.
                                .background(Ink950.copy(alpha = 0.92f))
                                .drawBehind {
                                    drawLine(
                                        color = GlassBorder,
                                        start = Offset(0f, 0f),
                                        end = Offset(size.width, 0f),
                                        strokeWidth = 1f
                                    )
                                }
                                .padding(horizontal = 24.dp, vertical = 12.dp),
                            horizontalArrangement = Arrangement.Center,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            ArrowButton(
                                icon = Icons.AutoMirrored.Outlined.KeyboardArrowLeft,
                                description = stringResource(Res.string.desktop_previous_page),
                                enabled = enabled && currentPage > 1,
                                onClick = { onPageSelected(currentPage - 1) }
                            )

                            Spacer(Modifier.width(8.dp))

                            pageWindow(currentPage, totalPages).forEach { slot ->
                                when (slot) {
                                    is PageSlot.Number -> PageChip(
                                        label = slot.page.toString(),
                                        selected = slot.page == currentPage,
                                        enabled = enabled,
                                        onClick = { onPageSelected(slot.page) }
                                    )

                                    PageSlot.Gap -> Text(
                                        text = "…",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = TextLow,
                                        modifier = Modifier.padding(horizontal = 6.dp)
                                    )
                                }
                                Spacer(Modifier.width(4.dp))
                            }

                            Spacer(Modifier.width(4.dp))

                            ArrowButton(
                                icon = Icons.AutoMirrored.Outlined.KeyboardArrowRight,
                                description = stringResource(Res.string.desktop_next_page),
                                enabled = enabled && currentPage < totalPages,
                                onClick = { onPageSelected(currentPage + 1) }
                            )

                            Spacer(Modifier.width(20.dp))

                            JumpToPage(totalPages = totalPages, enabled = enabled, onJump = onPageSelected)

                            Spacer(Modifier.width(12.dp))

                            Text(
                                text = stringResource(Res.string.desktop_page_of, currentPage, totalPages),
                                style = MaterialTheme.typography.bodySmall,
                                color = TextLow
                            )
                        }
                    }

                    private sealed interface PageSlot {
                        data class Number(val page: Int) : PageSlot
                        data object Gap : PageSlot
                    }

                    /**
                     * First, last, and a window around the current page, with gaps marked.
                     * e.g. current 42 of 1655 -> 1 … 40 41 [42] 43 44 … 1655
                     */
                    private fun pageWindow(current: Int, total: Int, radius: Int = 2): List<PageSlot> {
                        if (total <= 7) return (1..total).map { PageSlot.Number(it) }

                        val pages = sortedSetOf(1, total)
                        for (p in (current - radius)..(current + radius)) {
                            if (p in 1..total) pages.add(p)
                        }
                        // Keep the bar a stable width near the ends, where the window would otherwise be clipped.
                        if (current <= radius + 2) (1..(radius * 2 + 3)).forEach { if (it <= total) pages.add(it) }
                        if (current >= total - radius - 1) ((total - radius * 2 - 2)..total).forEach { if (it >= 1) pages.add(it) }

                        val slots = mutableListOf<PageSlot>()
                        var previous = 0
                        for (page in pages) {
                            if (previous != 0 && page - previous > 1) slots.add(PageSlot.Gap)
                            slots.add(PageSlot.Number(page))
                            previous = page
                        }
                        return slots
                    }

                    @Composable
                    private fun PageChip(
                        label: String,
                        selected: Boolean,
                        enabled: Boolean,
                        onClick: () -> Unit
                    ) {
                        val interaction = remember { MutableInteractionSource() }
                        val hovered by interaction.collectIsHoveredAsState()
                        val shape = RoundedCornerShape(8.dp)

                        val base = Modifier
                            .defaultMinSize(minWidth = 34.dp)
                            .height(32.dp)
                            .clip(shape)

                        Box(
                            modifier = when {
                                selected -> base.background(EmberGradient)
                                hovered && enabled -> base.background(Color.White.copy(alpha = 0.08f))
                                else -> base
                            }.desktopClickable(enabled = enabled && !selected, interaction = interaction, onClick = onClick),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = label,
                                style = MaterialTheme.typography.labelLarge,
                                color = if (selected) Color.White else if (hovered) TextHi else TextMid,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.padding(horizontal = 8.dp)
                            )
                        }
                    }

                    @Composable
                    private fun ArrowButton(
                        icon: ImageVector,
                        description: String,
                        enabled: Boolean,
                        onClick: () -> Unit
                    ) {
                        val interaction = remember { MutableInteractionSource() }
                        val hovered by interaction.collectIsHoveredAsState()
                        val shape = RoundedCornerShape(8.dp)

                        DesktopTooltip(text = description) {
                            Box(
                                modifier = Modifier
                                    .size(32.dp)
                                    .clip(shape)
                                    .then(
                                        if (hovered && enabled) Modifier.background(Color.White.copy(alpha = 0.08f))
                                        else Modifier
                                    )
                                    .desktopClickable(enabled = enabled, interaction = interaction, onClick = onClick),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    imageVector = icon,
                                    contentDescription = description,
                                    // Disabled arrows stay visible but obviously inert, rather than vanishing and
                                    // shifting the whole bar sideways at the first and last page.
                                    tint = if (!enabled) TextLow.copy(alpha = 0.4f) else if (hovered) TextHi else TextMid,
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                        }
                    }

                    @Composable
                    private fun JumpToPage(
                        totalPages: Int,
                        enabled: Boolean,
                        onJump: (Int) -> Unit
                    ) {
                        var text by remember { mutableStateOf("") }
                        val shape = RoundedCornerShape(8.dp)

                        fun submit() {
                            val page = text.toIntOrNull() ?: return
                            text = ""
                            onJump(page.coerceIn(1, totalPages))
                        }

                        Row(
                            modifier = Modifier.height(32.dp).width(66.dp).glass(shape),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                if (text.isEmpty()) {
                                    Text("Go to", style = MaterialTheme.typography.bodySmall, color = TextLow)
                                }
                                BasicTextField(
                                    value = text,
                                    // Digits only, so a stray letter cannot silently become a no-op submit.
                                    onValueChange = { input -> text = input.filter { it.isDigit() }.take(5) },
                                    enabled = enabled,
                                    singleLine = true,
                                    cursorBrush = SolidColor(Ember),
                                    textStyle = MaterialTheme.typography.bodySmall.copy(
                                        color = TextHi,
                                        textAlign = TextAlign.Center
                                    ),
                                    keyboardOptions = KeyboardOptions(
                                        keyboardType = KeyboardType.Number,
                                        imeAction = ImeAction.Go
                                    ),
                                    keyboardActions = KeyboardActions(onGo = { submit() }, onDone = { submit() }),
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )


            //https://github.com/laetuz/Neostore-Admin
            KotlinCode(
                code = """
                    @Composable
                    fun AppPagination(
                        currentPage: Int,
                        totalPages: Int,
                        onPageChange: (Int) -> Unit,
                        modifier: Modifier = Modifier,
                        containerColor: Color = DarkBackgroundV2,
                    ) {
                        val maxVisiblePages = 5
                        var startPage = maxOf(1, currentPage - maxVisiblePages / 2)
                        val endPage = minOf(totalPages, startPage + maxVisiblePages - 1)

                        if (endPage - startPage + 1 < maxVisiblePages) {
                            startPage = maxOf(1, endPage - maxVisiblePages + 1)
                        }

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .background(containerColor)
                                .padding(vertical = 8.dp),
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.Center,
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                if (currentPage > 1) {
                                    PageButton("<") { onPageChange(currentPage - 1) }
                                }

                                if (startPage > 1) {
                                    PageButton("1") { onPageChange(1) }
                                    if (startPage > 2) {
                                        Text("...", modifier = Modifier.padding(horizontal = 8.dp), color = Color.Gray)
                                    }
                                }

                                for (pageIndex in startPage..endPage) {
                                    PageButton(
                                        text = pageIndex.toString(),
                                        isSelected = pageIndex == currentPage,
                                        onClick = { onPageChange(pageIndex) },
                                    )
                                }

                                if (endPage < totalPages) {
                                    if (endPage < totalPages - 1) {
                                        Text("...", modifier = Modifier.padding(horizontal = 8.dp), color = Color.Gray)
                                    }
                                    PageButton(totalPages.toString()) { onPageChange(totalPages) }
                                }

                                if (currentPage < totalPages) {
                                    PageButton(">") { onPageChange(currentPage + 1) }
                                }
                            }
                        }
                    }

                    @Composable
                    private fun PageButton(text: String, isSelected: Boolean = false, onClick: () -> Unit) {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier
                                .padding(horizontal = 4.dp)
                                .clickable(enabled = !isSelected) { onClick() }

                                .border(
                                    width = 1.dp,
                                    color = if (isSelected) MaterialTheme.colorScheme.primary else Color.Gray,
                                    shape = RoundedCornerShape(8.dp),
                                )
                                .clip(
                                    shape = RoundedCornerShape(8.dp)
                                )
                                .background(color = DarkBackground)
                                .padding(horizontal = 16.dp, vertical = 8.dp),
                        ) {
                            Text(
                                text = text,
                                color = if (isSelected) MaterialTheme.colorScheme.primary else DarkPrimary,
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Paging 3",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data Layer: Where pages come from — a PagingSource (network or database) and optionally a RemoteMediator (network + database together)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel Layer: A Pager that produces a Flow<PagingData<T>> from your data sources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI Layer: LazyColumn with collectAsLazyPagingItems() that handles loading, displaying, and requesting the next page automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            }

            SpanText(
                text = "PagingSource",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The primary Paging library component in the repository layer is PagingSource. Each PagingSource object defines a source of data and how to retrieve data from that source. A PagingSource object can load data from any single source, including network sources and local databases. It's where we define how to load a page of data and how to determine the next and previous page keys.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The foundational element responsible for loading data in chunks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A PagingSource implementation involves overriding the load() function, which loads the data for a given page.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "RemoteMediator - Offline-First Pagination",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A RemoteMediator object handles paging from a layered data source, such as a network data source with a local database cache. (If we need to handle both local and remote data sources together (e.g., caching data in Room while fetching it from a network), we can use RemoteMediator alongside PagingSource.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User opens the app offline → Room has cached data → list shows immediately.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User opens the app online → Room shows cached data instantly → RemoteMediator silently fetches fresh data → Room updates → list updates automatically.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "getRefreshKey()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provides a key used for the initial load for the next PagingSource due to invalidation of this PagingSource.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The page number can be null if no pages have been loaded.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If prev is null; we are on the first page.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If next is null; we are at the last page.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If both are null; nothing has loaded and we are at the initial page.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "closestPageToPosition()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coerces an index in the list, including placeholders, to closest loaded page in pages. This function can be called with anchorPosition to fetch the loaded page that is closest to the last accessed index in the list. Returns: The closest loaded Value in pages to the provided anchorPosition. null if all loaded pages are empty.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "load()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "load(), function will be called by the Paging library to asynchronously fetch more data to be displayed as the user scrolls around. Data is retrieved either from a local database or a remote API, depending on the implementation. The load() method receives LoadParams as a parameter, which provides access to the current page key (key), the number of items to load (loadSize), and the placeholdersEnabled flag.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "LoadResult()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "After fetching the required data, LoadResult must be returned to the Paging framework. The LoadResult can be one of three types:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadResult.Page – Used when data is successfully loaded. It contains the list of items along with optional prevKey and nextKey.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadResult.Error – Used when an error occurs during data fetching.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadResult.Invalid – Used when the result is invalid. This return type can be used to terminate future load requests.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Pager",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Primary entry point into Paging; constructor for a reactive stream of PagingData. Each PagingData represents a snapshot of the backing paginated data. Updates to the backing dataset should be represented by a new instance of PagingData.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Pager component provides a public API for constructing instances of PagingData that are exposed in reactive streams, based on a PagingSource object and a PagingConfig configuration object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coordinates the interaction between the PagingSource and the UI layer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Configures the pagination parameters, such as page size, prefetch distance, and initial load size, providing fine-grained control over the loading behavior.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Three parameters are needed to create an instance. These are: PagingSource, PagingConfig and InitialKey is optional to start loading with a default key.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "PagingConfig",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "PagingConfig, this class sets options regarding how to load content from a PagingSource such as how far ahead to load, the size request for the initial load, and others. The only mandatory parameter you have to define is the page size.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "pagingSourceFactory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "pagingSourceFactory, function that defines how to create the PagingSource.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Loading States",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Paging library tracks the state of load requests for paged data and exposes it through the LoadState class. Your app can register a listener with the PagingDataAdapter to receive information about the current state and update the UI accordingly. These states are provided from the adapter because they are synchronous with the UI. This means that your listener receives updates when the page load has been applied to the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadStates.append: For the LoadState of items being fetched after the user's current position.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadStates.prepend: For the LoadState of items being fetched before the user's current position.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadStates.refresh: For the LoadState of the initial load.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://github.com/waqas028/Ktor-Koin-KMP
            KotlinCode(
                code = """
                    class GenericPagingDataSource<T : Any, R>(
                        private val apiService: suspend (R) -> List<T>,
                        private val requestData: R,
                        private val updateRequest: (R, Int) -> R,
                        private val errorState: MutableSharedFlow<String>
                    ) : PagingSource<Int, T>() {

                        override fun getRefreshKey(state: PagingState<Int, T>): Int? {
                            return state.anchorPosition?.let { anchorPosition ->
                                state.closestPageToPosition(anchorPosition)?.prevKey?.plus(1)
                                    ?: state.closestPageToPosition(anchorPosition)?.nextKey?.minus(1)
                            }
                        }

                        override suspend fun load(params: LoadParams<Int>): LoadResult<Int, T> {
                            return try {
                                val nextPage = params.key ?: 1
                                val updatedRequestData = updateRequest(requestData, nextPage)
                                val responseData = apiService(updatedRequestData)
                                LoadResult.Page(
                                    data = responseData,
                                    prevKey = if (nextPage == 1) null else nextPage - 1,
                                    nextKey = if (responseData.isNotEmpty()) nextPage + 1 else null
                                )
                            } catch (exception: HttpException) {
                                errorState.emit("Network error")
                                LoadResult.Error(exception)
                            } catch (exception: IOException) {
                                errorState.emit("Network error")
                                LoadResult.Error(exception)
                            } catch (exception: Exception) {
                                errorState.emit("An unexpected error occurred")
                                LoadResult.Error(exception)
                            }
                        }
                    }

                    data class ProductDTO(
                        val httpClient: HttpClient,
                        val offset: Int,
                        val limit: Int,
                    )

                    class GenericPagingRepository{
                        private val _errorState = MutableSharedFlow<String>()
                        val errorState: SharedFlow<String> = _errorState.asSharedFlow()

                        fun <T : Any, R> getPagingData(
                            requestData: R,
                            updateRequest: (R, Int) -> R,
                            fetchData: suspend (R) -> List<T>
                        ): Flow<PagingData<T>> = Pager(
                            config = PagingConfig(
                                pageSize = 10,
                                prefetchDistance = 2,
                                initialLoadSize = 10
                            ),
                            pagingSourceFactory = {
                                GenericPagingDataSource(
                                    apiService = fetchData,
                                    requestData = requestData,
                                    updateRequest = updateRequest,
                                    errorState = _errorState
                                )
                            }
                        ).flow
                    }

                    sealed interface Result<out D, out E: Error> {
                        data class Success<out D>(val data: D): Result<D, Nothing>
                        data class Error<out E: org.waqas028.ktor_kmp.data.util.Error>(val error: E): Result<Nothing, E>
                    }

                    inline fun <T, E: Error, R> Result<T, E>.map(map: (T) -> R): Result<R, E> {
                        return when(this) {
                            is Result.Error -> Result.Error(error)
                            is Result.Success -> Result.Success(map(data))
                        }
                    }

                    fun <T, E: Error> Result<T, E>.asEmptyDataResult(): EmptyResult<E> {
                        return map {  }
                    }

                    inline fun <T, E: Error> Result<T, E>.onSuccess(action: (T) -> Unit): Result<T, E> {
                        return when(this) {
                            is Result.Error -> this
                            is Result.Success -> {
                                action(data)
                                this
                            }
                        }
                    }
                    inline fun <T, E: Error> Result<T, E>.onError(action: (E) -> Unit): Result<T, E> {
                        return when(this) {
                            is Result.Error -> {
                                action(error)
                                this
                            }
                            is Result.Success -> this
                        }
                    }

                    typealias EmptyResult<E> = Result<Unit, E>

                    interface Error{}

                    class HomeVM(
                        private val homeRepo: HomeRepo,
                        private val genericPagingRepository: GenericPagingRepository,
                    ) : ViewModel() {
                        var isLoading by mutableStateOf(false)
                        var error by mutableStateOf("")

                        private val _productResponse = MutableStateFlow<List<Product>?>(null)
                        private val userRecentTaskUpdater = MutableStateFlow<ProductDTO?>(null)

                        @OptIn(ExperimentalCoroutinesApi::class)
                        var productList = userRecentTaskUpdater.filterNotNull().flatMapLatest { productDTO ->
                            genericPagingRepository.getPagingData(
                                requestData = productDTO,
                                updateRequest = { request, page -> request.copy(offset = page) },
                                fetchData = { request ->
                                    homeRepo.getProductList(request)
                                        .onSuccess {
                                            isLoading = false
                                            _productResponse.value = it
                                        }.onError {
                                            isLoading = false
                                            error = it.name
                                        }
                                    _productResponse.value.orEmpty()
                                }
                            )
                        }.cachedIn(viewModelScope)

                        fun getProductList(productDTO: ProductDTO) = viewModelScope.launch {
                            userRecentTaskUpdater.update { productDTO }
                        }
                    }

                    @Composable
                    fun HomeScreen(homeVM: HomeVM, client: HttpClient){
                        val error = homeVM.error
                        val loading = homeVM.isLoading
                        val productList = homeVM.productList.collectAsLazyPagingItems()

                        LaunchedEffect(Unit){
                            homeVM.getProductList(
                                productDTO = ProductDTO(
                                    httpClient = client,
                                    offset = 1,
                                    limit = 10
                                )
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/usarican/Clean-Architecture-Movie-Application
            KotlinCode(
                code = """
                    abstract class BasePagingSource<T:Any> : PagingSource<Int, T>() {
                        override fun getRefreshKey(state: PagingState<Int, T>): Int? {
                            return state.anchorPosition?.let { anchorPosition ->
                                val anchorPage = state.closestPageToPosition(anchorPosition)
                                anchorPage?.prevKey?.plus(1) ?: anchorPage?.nextKey?.minus(1)
                            }
                        }

                        override suspend fun load(params: LoadParams<Int>): LoadResult<Int, T> {
                            return try {
                                executeLoad(params)
                            } catch (exception: IOException) {
                                LoadResult.Error(exception)
                            } catch (exception: HttpException) {
                                LoadResult.Error(exception)
                            }
                        }

                        abstract suspend fun executeLoad(params: LoadParams<Int>): LoadResult<Int, T>
                    }

                    class MovieReviewsSeeAllPagingSource(
                        private val detailRemoteDataSource: DetailRemoteDataSource,
                        private val movieId: Int,
                    ) : BasePagingSource<AuthorResponse>() {
                        override suspend fun executeLoad(params: LoadParams<Int>): LoadResult<Int, AuthorResponse> {
                            val nextPageNumber = params.key ?: STARTING_PAGE_INDEX
                            val response =
                                detailRemoteDataSource.getMovieReviews(movieId = movieId, page = nextPageNumber)
                            return LoadResult.Page(
                                data = response.authorResponses,
                                prevKey = if (nextPageNumber == STARTING_PAGE_INDEX) null else nextPageNumber - 1,
                                nextKey = if (response.authorResponses.isEmpty()) null else response.page + 1
                            )
                        }
                    }

                    class DetailRemoteDataSource @Inject constructor(
                        private val movieDetailService: MovieDetailService
                    ) {
                        suspend fun getMovieReviews(movieId: Int, page: Int = STARTING_PAGE_INDEX) = movieDetailService.getMovieReviewsByMovieId(movieId,page)
                    }

                    interface SeeAllRepository {
                        fun getMovieReviewsSeeAll(movieId: Int) : Flow<PagingData<AuthorResponse>>
                    }

                    class SeeAllRepositoryImpl @Inject constructor(
                        private val movieRemoteDataSource: MovieRemoteDataSource,
                        private val movieLocalDataSource: MovieLocalDataSource,
                        private val movieEntityToResponseMapper: MovieEntityToResponseMapper,
                        private val detailRemoteDataSource: DetailRemoteDataSource
                    ) : SeeAllRepository {
                        override fun getMovieReviewsSeeAll(movieId: Int): Flow<PagingData<AuthorResponse>> {
                            return Pager(config = PagingConfig(pageSize = MOVIE_PAGE_SIZE), pagingSourceFactory = {
                                MovieReviewsSeeAllPagingSource(
                                    movieId = movieId,
                                    detailRemoteDataSource = detailRemoteDataSource
                                )
                            }).flow
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/yassineAbou/LLMS
            KotlinCode(
                code = """
                    //Horizontal Pagination

                    @Composable
                    fun Modifier.draggableScrollModifier(
                        scrollState: LazyListState,
                        isEnabled: Boolean = true
                    ): Modifier {
                        val coroutineScope = rememberCoroutineScope()
                        val isDesktopOrWasm = PlatformConfig.isDesktop() or PlatformConfig.isWasm()

                        return if (isEnabled && isDesktopOrWasm) {
                            this.draggable(
                                orientation = Orientation.Horizontal,
                                state = rememberDraggableState { delta ->
                                    coroutineScope.launch {
                                        scrollState.scrollBy(-delta) // Scroll horizontally based on drag delta
                                    }
                                }
                            )
                        } else {
                            this // Return the original Modifier if not enabled or not on desktop/WASM
                        }
                    }

                    @Composable
                    private fun Inspirations(
                        loadedInspiration: List<UrlExample>,
                        loadNextPage: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val scrollState = rememberLazyListState()
                        val coroutineScope = rememberCoroutineScope()

                        val shouldLoadMore by remember(scrollState, loadedInspiration) {
                            derivedStateOf {
                                val layoutInfo = scrollState.layoutInfo
                                val visibleItems = layoutInfo.visibleItemsInfo

                                if (loadedInspiration.isEmpty()) return@derivedStateOf false

                                val lastVisibleIndex = visibleItems.lastOrNull()?.index ?: 0
                                val totalItemsCount = loadedInspiration.size // Directly use the input list size

                                // Trigger load when reaching last 2 items (pageSize = 5 in ViewModel)
                                lastVisibleIndex >= totalItemsCount - 2 &&
                                        !scrollState.isScrollInProgress
                            }
                        }

                        LaunchedEffect(shouldLoadMore) {
                            if (shouldLoadMore) {
                                loadNextPage()
                            }
                        }

                        Box(modifier = modifier) {
                            LazyRow(
                                state = scrollState,
                                modifier = Modifier
                                    .padding(top = 8.dp)
                                    .draggableScrollModifier(scrollState), // Apply the reusable drag modifier for desktop and wasm
                                contentPadding = PaddingValues(horizontal = 16.dp),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                items(
                                    items = loadedInspiration,
                                    key = { item -> item.id }
                                ) { inspiration ->
                                    //UI
                                }
                            }
                        }
                    }

                    private var currentPage = 0
                    private val pageSize = 5 // Adjust based on performance needs

                    fun loadNextInspirationPage() {
                        val start = currentPage * pageSize
                        if (start >= fullInspirationList.size) return

                        val end = min(start + pageSize, fullInspirationList.size)
                        val nextPageItems = fullInspirationList.subList(start, end)

                        // Fix 2: Update using update operator for StateFlow
                         _loadedInspiration.update { currentList ->
                                currentList + nextPageItems
                        }
                        currentPage++
                    }

                    @Immutable
                    data class UrlExample @OptIn(ExperimentalUuidApi::class) constructor(
                        val id: String = Uuid.random().toString(),
                        val url: String = "",
                        val description: String = ""
                    )
                    """.trimIndent()
            )

            //https://github.com/rachel-ylcs/ylcs-kmp
            KotlinCode(
                code = """
                    //Custom Pagination

                    @Composable
                    fun LoadingAnimation(
                    	size: Dp = ThemeValue.Size.Icon,
                    	color: Color = MaterialTheme.colorScheme.primary,
                    	duration: Int = app.config.animationSpeed,
                    	num: Int = 3,
                    	modifier: Modifier = Modifier
                    ) {
                    	val transition = rememberInfiniteTransition()
                    	val values = Array(num) {
                    		transition.animateFloat(0.3f, 1f, infiniteRepeatable(
                    			animation = tween(duration, it * duration / num, LinearEasing),
                    			repeatMode = RepeatMode.Reverse
                    		))
                    	}

                    	Canvas(modifier = modifier.padding(ThemeValue.Padding.InnerIcon).size(size)) {
                    		values.forEachIndexed { index, state ->
                    			val width = (size / 5).toPx()
                    			val spacing = (this.size.width - (num * width)) / 2
                    			scale(scaleX = 1f, scaleY = state.value) {
                    				drawLine(
                    					color = color.copy(alpha = state.value),
                    					start = Offset(width / 2 + (width + spacing) * index, 0f),
                    					end = Offset(width / 2 + (width + spacing) * index, this.size.height),
                    					strokeWidth = width
                    				)
                    			}
                    		}
                    	}
                    }

                    @Stable
                    abstract class Pagination<E, out T>(
                    	private val default: T,
                    	val pageNum: Int = 20
                    ) {
                    	val items = mutableStateListOf<E>()
                    	var canLoading by mutableStateOf(false)

                    	private var mOffset: T = default
                    	abstract fun offset(item: E): T
                    	val offset: T get() = mOffset

                    	open fun processArgs(last: E?) {}

                    	fun newData(newItems: List<E>): Boolean {
                    		items.replaceAll(newItems)
                    		val last = newItems.lastOrNull()
                    		mOffset = last?.let { offset(it) } ?: default
                    		processArgs(last)
                    		canLoading = newItems.size == pageNum
                    		return newItems.isNotEmpty()
                    	}

                    	fun moreData(newItems: List<E>): Boolean {
                    		if (newItems.isEmpty()) {
                    			mOffset = default
                    			processArgs(null)
                    		}
                    		else {
                    			items += newItems
                    			val last = newItems.lastOrNull()
                    			mOffset = last?.let { offset(it) } ?: default
                    			processArgs(last)
                    		}
                    		canLoading = offset != default && newItems.size == pageNum
                    		return newItems.isNotEmpty()
                    	}
                    }

                    @Stable
                    abstract class PaginationArgs<E, out T, out A1>(
                    	default: T,
                    	private val default1: A1,
                    	pageNum: Int = 20
                    ) : Pagination<E, T>(default, pageNum) {
                    	private var mArg1: A1 = default1
                    	abstract fun arg1(item: E): A1
                    	val arg1: A1 get() = mArg1

                    	override fun processArgs(last: E?) {
                    		mArg1 = last?.let { arg1(it) } ?: default1
                    	}
                    }

                    @Stable
                    @Serializable
                    private enum class PaginationStatus {
                    	IDLE, RUNNING, PULL, RELEASE
                    }

                    @Stable
                    private class SwipeState {
                    	var isReleaseEdge = false
                    	var refreshStatus by mutableStateOf(PaginationStatus.IDLE)
                    	var loadingStatus by mutableStateOf(PaginationStatus.IDLE)
                    	var isAnimateOver by mutableStateOf(true)
                    	val isRunning: Boolean get() = !isAnimateOver || refreshStatus == PaginationStatus.RUNNING || loadingStatus == PaginationStatus.RUNNING

                    	private val mutatorMutex = MutatorMutex()
                    	private val _indicatorOffset = Animatable(0f)
                    	val indicatorOffset: Float get() = _indicatorOffset.value

                    	suspend fun animateOffsetTo(offset: Float) = mutatorMutex.mutate {
                    		_indicatorOffset.animateTo(offset) {
                    			if (this.value == 0f) isAnimateOver = true
                    		}
                    	}

                    	suspend fun snapOffsetTo(headerHeightPx: Float, footerHeightPx: Float, offset: Float) = mutatorMutex.mutate(MutatePriority.UserInput) {
                    		_indicatorOffset.snapTo(offset)
                    		if (indicatorOffset >= headerHeightPx) refreshStatus = PaginationStatus.RELEASE
                    		else if (indicatorOffset <= -footerHeightPx) loadingStatus = PaginationStatus.RELEASE
                    		else {
                    			if (indicatorOffset > 0) refreshStatus = PaginationStatus.PULL
                    			if (indicatorOffset < 0) loadingStatus = PaginationStatus.PULL
                    		}
                    	}
                    }

                    @Composable
                    private fun DefaultSwipePaginationHeader(
                    	status: PaginationStatus,
                    	progress: Float
                    ) {
                    	Row(
                    		modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.primaryContainer.copy(alpha = progress)),
                    		verticalAlignment = Alignment.CenterVertically,
                    		horizontalArrangement = Arrangement.spacedBy(ThemeValue.Padding.HorizontalExtraSpace, Alignment.CenterHorizontally)
                    	) {
                    		if (status == PaginationStatus.RUNNING) LoadingAnimation(
                    			size = ThemeValue.Size.ExtraIcon,
                    			color = MaterialTheme.colorScheme.onPrimaryContainer
                    		)
                    		else MiniIcon(
                    			icon = Icons.Outlined.ArrowDownward,
                    			size = ThemeValue.Size.ExtraIcon,
                    			color = MaterialTheme.colorScheme.onPrimaryContainer
                    		)
                    		Text(
                    			text = when (status) {
                    				PaginationStatus.RUNNING -> "Refreshing..."
                    				PaginationStatus.PULL -> "Continue pulling down to refresh"
                    				PaginationStatus.RELEASE -> "Release refresh immediately"
                    				else -> ""
                    			},
                    			color = MaterialTheme.colorScheme.onPrimaryContainer
                    		)
                    	}
                    }

                    @Composable
                    private fun DefaultSwipePaginationFooter(
                    	status: PaginationStatus,
                    	progress: Float
                    ) {
                    	Row(
                    		modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.primaryContainer.copy(alpha = progress)),
                    		verticalAlignment = Alignment.CenterVertically,
                    		horizontalArrangement = Arrangement.spacedBy(ThemeValue.Padding.HorizontalExtraSpace, Alignment.CenterHorizontally)
                    	) {
                    		if (status == PaginationStatus.RUNNING) LoadingAnimation(
                    			size = ThemeValue.Size.ExtraIcon,
                    			color = MaterialTheme.colorScheme.onPrimaryContainer
                    		)
                    		else MiniIcon(
                    			icon = Icons.Outlined.ArrowUpward,
                    			size = ThemeValue.Size.ExtraIcon,
                    			color = MaterialTheme.colorScheme.onPrimaryContainer
                    		)
                    		Text(
                    			text = when (status) {
                    				PaginationStatus.RUNNING -> "Loading..."
                    				PaginationStatus.PULL -> "Pull up to load more"
                    				PaginationStatus.RELEASE -> "Release load immediately"
                    				else -> ""
                    			},
                    			color = MaterialTheme.colorScheme.onPrimaryContainer
                    		)
                    	}
                    }

                    @Composable
                    private fun SwipePaginationLayout(
                    	canRefresh: Boolean = true,
                    	canLoading: Boolean = false,
                    	onRefresh: (suspend () -> Unit)? = null,
                    	onLoading: (suspend () -> Unit)? = null,
                    	headerHeight: Dp = ThemeValue.Size.RefreshHeaderHeight,
                    	header: @Composable (PaginationStatus, Float) -> Unit = { status, progress -> DefaultSwipePaginationHeader(status, progress) },
                    	footerHeight: Dp = ThemeValue.Size.RefreshFooterHeight,
                    	footer: @Composable (PaginationStatus, Float) -> Unit = { status, progress -> DefaultSwipePaginationFooter(status, progress) },
                    	stickinessLevel: Float = 0.5f,
                    	modifier: Modifier = Modifier,
                    	content: @Composable BoxScope.() -> Unit
                    ) {
                    	val scope = rememberCoroutineScope()
                    	val state = remember { SwipeState() }
                    	val (headerHeightPx, footerHeightPx) = with(LocalDensity.current) {
                    		headerHeight.toPx() to footerHeight.toPx()
                    	}
                    	val connection = remember(
                    		canRefresh, canLoading, stickinessLevel, state, headerHeightPx, footerHeightPx
                    	) { object : NestedScrollConnection {
                    		private fun scroll(canConsumed: Float): Offset = if (canConsumed.absoluteValue > 0.5f) {
                    			scope.launch {
                    				state.snapOffsetTo(headerHeightPx, footerHeightPx, state.indicatorOffset + canConsumed)
                    			}
                    			Offset(0f, canConsumed / stickinessLevel)
                    		} else Offset.Zero

                    		override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset = when {
                    			state.isRunning -> Offset.Zero
                    			available.y < 0 && state.indicatorOffset > 0 -> scroll((available.y * stickinessLevel).coerceAtLeast(-state.indicatorOffset))
                    			available.y > 0 && state.indicatorOffset < 0 -> scroll((available.y * stickinessLevel).coerceAtMost(-state.indicatorOffset))
                    			else -> Offset.Zero
                    		}

                    		override fun onPostScroll(consumed: Offset, available: Offset, source: NestedScrollSource): Offset = when {
                    			state.isRunning -> Offset.Zero
                    			available.y > 0 && canRefresh -> scroll(canConsumed =
                    				if (source == NestedScrollSource.SideEffect) (available.y * stickinessLevel).coerceAtMost(-state.indicatorOffset)
                    				else (available.y * stickinessLevel).coerceAtMost(headerHeightPx - state.indicatorOffset)
                    			)
                    			available.y < 0 && canLoading -> scroll(canConsumed =
                    				if (source == NestedScrollSource.SideEffect) (available.y * stickinessLevel).coerceAtLeast(-state.indicatorOffset)
                    				else (available.y * stickinessLevel).coerceAtLeast(-footerHeightPx - state.indicatorOffset)
                    			)
                    			else -> Offset.Zero
                    		}

                    		override suspend fun onPreFling(available: Velocity): Velocity {
                    			if (state.isRunning) return Velocity.Zero
                    			state.isReleaseEdge = state.indicatorOffset != 0f
                    			return if (state.indicatorOffset >= headerHeightPx && state.isReleaseEdge && state.refreshStatus != PaginationStatus.RUNNING) {
                    				state.isAnimateOver = false
                    				state.refreshStatus = PaginationStatus.RUNNING
                    				state.animateOffsetTo(headerHeightPx)
                    				onRefresh?.invoke()
                    				state.refreshStatus = PaginationStatus.IDLE
                    				state.animateOffsetTo(0f)
                    				available
                    			}
                    			else if (state.indicatorOffset <= -footerHeightPx && state.isReleaseEdge && state.loadingStatus != PaginationStatus.RUNNING) {
                    				state.isAnimateOver = false
                    				state.loadingStatus = PaginationStatus.RUNNING
                    				state.animateOffsetTo(-footerHeightPx)
                    				onLoading?.invoke()
                    				state.loadingStatus = PaginationStatus.IDLE
                    				state.animateOffsetTo(0f)
                    				available
                    			}
                    			else super.onPreFling(available)
                    		}

                    		override suspend fun onPostFling(consumed: Velocity, available: Velocity): Velocity {
                    			if (state.isRunning) return Velocity.Zero
                    			if (state.indicatorOffset > 0 && state.refreshStatus != PaginationStatus.RUNNING) {
                    				state.refreshStatus = PaginationStatus.IDLE
                    				state.animateOffsetTo(0f)
                    			}
                    			else if (state.indicatorOffset < 0 && state.loadingStatus != PaginationStatus.RUNNING) {
                    				state.loadingStatus = PaginationStatus.IDLE
                    				state.animateOffsetTo(0f)
                    			}
                    			return super.onPostFling(consumed, available)
                    		}
                    	} }

                    	Box(modifier = modifier.clipToBounds().nestedScroll(connection)) {
                    		Box(
                    			modifier = Modifier.fillMaxWidth().height(headerHeight).align(Alignment.TopCenter)
                    				.graphicsLayer { translationY = -headerHeightPx + state.indicatorOffset }
                    		) {
                    			header(state.refreshStatus, abs(state.indicatorOffset) / headerHeightPx)
                    		}
                    		Box(
                    			modifier = Modifier.fillMaxSize().graphicsLayer { translationY = state.indicatorOffset },
                    			content = content
                    		)
                    		Box(
                    			modifier = Modifier.fillMaxWidth().height(footerHeight).align(Alignment.BottomCenter)
                    				.graphicsLayer { translationY = footerHeightPx + state.indicatorOffset }
                    		) {
                    			footer(state.loadingStatus, -state.indicatorOffset / footerHeightPx)
                    		}
                    	}
                    }

                    @Composable
                    private fun DefaultClickPaginationIndicator(
                    	status: PaginationStatus,
                    	onLoading: () -> Unit
                    ) {
                    	Box(
                    		modifier = Modifier.fillMaxWidth().clickable(onClick = onLoading),
                    		contentAlignment = Alignment.Center
                    	) {
                    		Row(
                    			modifier = Modifier.padding(ThemeValue.Padding.EqualValue),
                    			verticalAlignment = Alignment.CenterVertically,
                    			horizontalArrangement = Arrangement.spacedBy(ThemeValue.Padding.HorizontalExtraSpace, Alignment.CenterHorizontally)
                    		) {
                    			if (status == PaginationStatus.RUNNING) LoadingAnimation(size = ThemeValue.Size.ExtraIcon)
                    			else MiniIcon(icon = Icons.Filled.Update, size = ThemeValue.Size.ExtraIcon)
                    			Text(text = if (status == PaginationStatus.RUNNING) "Loading..." else "Load more")
                    		}
                    	}
                    }

                    @Composable
                    private fun <T> ClickPaginationColumn(
                    	items: List<T>,
                    	key: ((T) -> Any)? = null,
                    	state: LazyListState = rememberLazyListState(),
                    	canLoading: Boolean = false,
                    	onLoading: (suspend () -> Unit)? = null,
                    	indicator: @Composable (PaginationStatus, () -> Unit) -> Unit = { status, onClick -> DefaultClickPaginationIndicator(status, onClick) },
                    	modifier: Modifier = Modifier,
                    	contentPadding: PaddingValues = ThemeValue.Padding.ZeroValue,
                    	verticalArrangement: Arrangement.Vertical = Arrangement.Top,
                    	horizontalAlignment: Alignment.Horizontal = Alignment.Start,
                    	header: (@Composable LazyItemScope.() -> Unit)? = null,
                    	itemDivider: PaddingValues? = null,
                    	itemContent: @Composable LazyItemScope.(T) -> Unit
                    ) {
                    	val scope = rememberCoroutineScope()
                    	var status by rememberState { PaginationStatus.IDLE }
                    	LazyColumn(
                    		state = state,
                    		contentPadding = contentPadding,
                    		verticalArrangement = verticalArrangement,
                    		horizontalAlignment = horizontalAlignment,
                    		modifier = modifier
                    	) {
                    		if (header != null) {
                    			item(key = ItemKey("Header")) {
                    				header()
                    			}
                    		}
                    		itemsIndexed(items = items, key = key?.let { { index, item -> it(item) } }) {index, item->
                    			if (itemDivider != null && index != 0) HorizontalDivider(modifier = Modifier.padding(itemDivider))
                    			itemContent(item)
                    		}
                    		if (canLoading) {
                    			item(key = Unit) {
                    				indicator(status) {
                    					if (status != PaginationStatus.RUNNING) {
                    						scope.launch {
                    							status = PaginationStatus.RUNNING
                    							onLoading?.invoke()
                    							status = PaginationStatus.IDLE
                    						}
                    					}
                    				}
                    			}
                    		}
                    	}
                    }

                    @Composable
                    private fun <T> ClickPaginationGrid(
                    	items: List<T>,
                    	key: ((T) -> Any)? = null,
                    	columns: GridCells,
                    	state: LazyGridState = rememberLazyGridState(),
                    	canLoading: Boolean = false,
                    	onLoading: (suspend () -> Unit)? = null,
                    	indicator: @Composable (PaginationStatus, () -> Unit) -> Unit = { status, onClick -> DefaultClickPaginationIndicator(status, onClick) },
                    	modifier: Modifier = Modifier,
                    	contentPadding: PaddingValues = ThemeValue.Padding.ZeroValue,
                    	verticalArrangement: Arrangement.Vertical = Arrangement.Top,
                    	horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
                    	header: (@Composable LazyGridItemScope.() -> Unit)? = null,
                    	itemContent: @Composable LazyGridItemScope.(T) -> Unit
                    ) {
                    	val scope = rememberCoroutineScope()
                    	var status by rememberState { PaginationStatus.IDLE }
                    	LazyVerticalGrid(
                    		columns = columns,
                    		state = state,
                    		contentPadding = contentPadding,
                    		verticalArrangement = verticalArrangement,
                    		horizontalArrangement = horizontalArrangement,
                    		modifier = modifier
                    	) {
                    		if (header != null) {
                    			item(
                    				key = ItemKey("Header"),
                    				span = { GridItemSpan(maxLineSpan) }
                    			) {
                    				header()
                    			}
                    		}
                    		items(items = items, key = key, itemContent = itemContent)
                    		if (canLoading) {
                    			item(
                    				key = Unit,
                    				span = { GridItemSpan(maxLineSpan) }
                    			) {
                    				indicator(status) {
                    					if (status != PaginationStatus.RUNNING) {
                    						scope.launch {
                    							status = PaginationStatus.RUNNING
                    							onLoading?.invoke()
                    							status = PaginationStatus.IDLE
                    						}
                    					}
                    				}
                    			}
                    		}
                    	}
                    }

                    @Composable
                    private fun <T> ClickPaginationStaggeredGrid(
                    	items: List<T>,
                    	key: ((T) -> Any)? = null,
                    	columns: StaggeredGridCells,
                    	state: LazyStaggeredGridState = rememberLazyStaggeredGridState(),
                    	canLoading: Boolean = false,
                    	onLoading: (suspend () -> Unit)? = null,
                    	indicator: @Composable (PaginationStatus, () -> Unit) -> Unit = { status, onClick -> DefaultClickPaginationIndicator(status, onClick) },
                    	modifier: Modifier = Modifier,
                    	contentPadding: PaddingValues = ThemeValue.Padding.ZeroValue,
                    	verticalItemSpacing: Dp = ThemeValue.Padding.ZeroSpace,
                    	horizontalArrangement: Arrangement.Horizontal = Arrangement.spacedBy(ThemeValue.Padding.ZeroSpace),
                    	header: (@Composable LazyStaggeredGridItemScope.() -> Unit)? = null,
                    	itemContent: @Composable LazyStaggeredGridItemScope.(T) -> Unit
                    ) {
                    	val scope = rememberCoroutineScope()
                    	var status by rememberState { PaginationStatus.IDLE }
                    	LazyVerticalStaggeredGrid(
                    		columns = columns,
                    		state = state,
                    		contentPadding = contentPadding,
                    		verticalItemSpacing = verticalItemSpacing,
                    		horizontalArrangement = horizontalArrangement,
                    		modifier = modifier
                    	) {
                    		if (header != null) {
                    			item(
                    				key = ItemKey("Header"),
                    				span = StaggeredGridItemSpan.FullLine
                    			) {
                    				header()
                    			}
                    		}
                    		items(items = items, key = key, itemContent = itemContent)
                    		if (canLoading) {
                    			item(
                    				key = Unit,
                    				span = StaggeredGridItemSpan.FullLine
                    			) {
                    				indicator(status) {
                    					if (status != PaginationStatus.RUNNING) {
                    						scope.launch {
                    							status = PaginationStatus.RUNNING
                    							onLoading?.invoke()
                    							status = PaginationStatus.IDLE
                    						}
                    					}
                    				}
                    			}
                    		}
                    	}
                    }

                    @Composable
                    fun <T> PaginationColumn(
                    	items: List<T>,
                    	key: ((T) -> Any)? = null,
                    	state: LazyListState = rememberLazyListState(),
                    	canRefresh: Boolean = true,
                    	canLoading: Boolean = false,
                    	onRefresh: (suspend () -> Unit)? = null,
                    	onLoading: (suspend () -> Unit)? = null,
                    	modifier: Modifier = Modifier,
                    	contentPadding: PaddingValues = ThemeValue.Padding.ZeroValue,
                    	verticalArrangement: Arrangement.Vertical = Arrangement.Top,
                    	horizontalAlignment: Alignment.Horizontal = Alignment.Start,
                    	header: (@Composable LazyItemScope.() -> Unit)? = null,
                    	itemDivider: PaddingValues? = null,
                    	itemContent: @Composable LazyItemScope.(T) -> Unit
                    ) {
                    	OS.ifPlatform(
                    		*Platform.Phone,
                    		ifTrue = {
                    			SwipePaginationLayout(
                    				canRefresh = canRefresh,
                    				canLoading = canLoading,
                    				onRefresh = onRefresh,
                    				onLoading = onLoading,
                    				modifier = modifier
                    			) {
                    				LazyColumn(
                    					modifier = Modifier.fillMaxSize(),
                    					state = state,
                    					contentPadding = contentPadding,
                    					verticalArrangement = verticalArrangement,
                    					horizontalAlignment = horizontalAlignment,
                    				) {
                    					if (header != null) {
                    						item(key = ItemKey("Header")) {
                    							header()
                    						}
                    					}
                    					itemsIndexed(items = items, key = key?.let { { index, item -> it(item) } }) {index, item->
                    						if (itemDivider != null && index != 0) HorizontalDivider(modifier = Modifier.padding(itemDivider))
                    						itemContent(item)
                    					}
                    				}
                    			}
                    		},
                    		ifFalse = {
                    			ClickPaginationColumn(
                    				items = items,
                    				key = key,
                    				state = state,
                    				canLoading = canLoading,
                    				onLoading = onLoading,
                    				modifier = modifier,
                    				contentPadding = contentPadding,
                    				verticalArrangement = verticalArrangement,
                    				horizontalAlignment = horizontalAlignment,
                    				header = header,
                    				itemDivider = itemDivider,
                    				itemContent = itemContent
                    			)
                    		}
                    	)
                    }

                    @Composable
                    fun <T> PaginationGrid(
                    	items: List<T>,
                    	key: ((T) -> Any)? = null,
                    	columns: GridCells,
                    	state: LazyGridState = rememberLazyGridState(),
                    	canRefresh: Boolean = true,
                    	canLoading: Boolean = false,
                    	onRefresh: (suspend () -> Unit)? = null,
                    	onLoading: (suspend () -> Unit)? = null,
                    	modifier: Modifier = Modifier,
                    	contentPadding: PaddingValues = ThemeValue.Padding.ZeroValue,
                    	verticalArrangement: Arrangement.Vertical = Arrangement.Top,
                    	horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
                    	header: (@Composable LazyGridItemScope.() -> Unit)? = null,
                    	itemContent: @Composable LazyGridItemScope.(T) -> Unit
                    ) {
                    	OS.ifPlatform(
                    		*Platform.Phone,
                    		ifTrue = {
                    			SwipePaginationLayout(
                    				canRefresh = canRefresh,
                    				canLoading = canLoading,
                    				onRefresh = onRefresh,
                    				onLoading = onLoading,
                    				modifier = modifier
                    			) {
                    				LazyVerticalGrid(
                    					columns = columns,
                    					modifier = Modifier.fillMaxSize(),
                    					state = state,
                    					contentPadding = contentPadding,
                    					verticalArrangement = verticalArrangement,
                    					horizontalArrangement = horizontalArrangement,
                    				) {
                    					if (header != null) {
                    						item(
                    							key = ItemKey("Header"),
                    							span = { GridItemSpan(maxLineSpan) }
                    						) {
                    							header()
                    						}
                    					}
                    					items(items = items, key = key, itemContent = itemContent)
                    				}
                    			}
                    		},
                    		ifFalse = {
                    			ClickPaginationGrid(
                    				items = items,
                    				key = key,
                    				columns = columns,
                    				state = state,
                    				canLoading = canLoading,
                    				onLoading = onLoading,
                    				modifier = modifier,
                    				contentPadding = contentPadding,
                    				verticalArrangement = verticalArrangement,
                    				horizontalArrangement = horizontalArrangement,
                    				header = header,
                    				itemContent = itemContent
                    			)
                    		}
                    	)
                    }

                    @Composable
                    fun <T> PaginationStaggeredGrid(
                    	items: List<T>,
                    	key: ((T) -> Any)? = null,
                    	columns: StaggeredGridCells,
                    	state: LazyStaggeredGridState = rememberLazyStaggeredGridState(),
                    	canRefresh: Boolean = true,
                    	canLoading: Boolean = false,
                    	onRefresh: (suspend () -> Unit)? = null,
                    	onLoading: (suspend () -> Unit)? = null,
                    	modifier: Modifier = Modifier,
                    	contentPadding: PaddingValues = ThemeValue.Padding.ZeroValue,
                    	verticalItemSpacing: Dp = ThemeValue.Padding.ZeroSpace,
                    	horizontalArrangement: Arrangement.Horizontal = Arrangement.spacedBy(ThemeValue.Padding.ZeroSpace),
                    	header: (@Composable LazyStaggeredGridItemScope.() -> Unit)? = null,
                    	itemContent: @Composable LazyStaggeredGridItemScope.(T) -> Unit
                    ) {
                    	OS.ifPlatform(
                    		*Platform.Phone,
                    		ifTrue = {
                    			SwipePaginationLayout(
                    				canRefresh = canRefresh,
                    				canLoading = canLoading,
                    				onRefresh = onRefresh,
                    				onLoading = onLoading,
                    				modifier = modifier
                    			) {
                    				LazyVerticalStaggeredGrid(
                    					columns = columns,
                    					modifier = Modifier.fillMaxSize(),
                    					state = state,
                    					contentPadding = contentPadding,
                    					verticalItemSpacing = verticalItemSpacing,
                    					horizontalArrangement = horizontalArrangement
                    				) {
                    					if (header != null) {
                    						item(
                    							key = ItemKey("Header"),
                    							span = StaggeredGridItemSpan.FullLine
                    						) {
                    							header()
                    						}
                    					}
                    					items(items = items, key = key, itemContent = itemContent)
                    				}
                    			}
                    		},
                    		ifFalse = {
                    			ClickPaginationStaggeredGrid(
                    				items = items,
                    				key = key,
                    				columns = columns,
                    				state = state,
                    				canLoading = canLoading,
                    				onLoading = onLoading,
                    				modifier = modifier,
                    				contentPadding = contentPadding,
                    				verticalItemSpacing = verticalItemSpacing,
                    				horizontalArrangement = horizontalArrangement,
                    				header = header,
                    				itemContent = itemContent
                    			)
                    		}
                    	)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //GridView Pagination
                    
                    @Composable
                    fun PetListScreen(
                        navController: NavHostController,
                        viewModel: PetListViewModel = koinInject()
                    ) {
                        val lazyGridState = rememberLazyGridState()
                        val uiState = viewModel.uiState

                        LaunchedEffect(lazyGridState, uiState.isLoading) {
                            snapshotFlow { lazyGridState.layoutInfo.visibleItemsInfo.lastOrNull()?.index }
                                .collect { lastVisibleItemIndex ->
                                    if (lastVisibleItemIndex == uiState.pets.size - 1 && !uiState.isLoading) {
                                        viewModel.getPets()
                                    }
                                }
                        }
                    }
                    
                    class PetListViewModel() : ViewModel() {
                        var uiState by mutableStateOf(PetListScreenState())
                            private set
                    }
                    
                    data class PetListScreenState(
                        val pets: List<Pet> = emptyList(),
                        val pageData: PageData = PageData(0,0,0,0),
                        val isLoading: Boolean = false,
                        val hasError: Boolean = false
                    )
                    """.trimIndent()
            )

            //https://github.com/robdich/Pokedex-KMP
            KotlinCode(
                code = """
                    val buffer = 1
                    val listState: LazyListState = rememberLazyListState()

                    // observe list scrolling
                    val reachedBottom: Boolean by remember {
                        derivedStateOf {
                            val lastVisibleItem = listState.layoutInfo.visibleItemsInfo.lastOrNull()
                            lastVisibleItem?.index != 0 && lastVisibleItem?.index == listState.layoutInfo.totalItemsCount - buffer
                        }
                    }

                    // load more if scrolled to bottom
                    LaunchedEffect(reachedBottom) {
                        if (reachedBottom) {
                            loadPokemonList()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Pagination cachedIn",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/MuhammetKonukcu/SpeciFind
            KotlinCode(
                code = """
                    class HistoryViewModel(private val localRepo: HistoryLocalRepository) : ViewModel() {
                        val historyPagingDataFlow: Flow<PagingData<HistoryEntity>> =
                            localRepo.getAllHistory()
                                .cachedIn(viewModelScope)
                                .map { data ->
                                    data.map {
                                        it.copy(
                                            language = it.language?.uppercase(),
                                            fileType = it.fileType?.replaceFirstChar { it.titlecase() }
                                        )
                                    }
                                }
                    }
                    
                    class HistoryLocalRepositoryImpl(private val historyDao: HistoryDao) : HistoryLocalRepository {
                        override fun getAllHistory(pageSize: Int): Flow<PagingData<HistoryEntity>> {
                            return Pager(
                                config = PagingConfig(
                                    pageSize = pageSize,
                                    enablePlaceholders = false
                                ),
                                pagingSourceFactory = { historyDao.getAllHistory() }
                            ).flow
                        }
                    }
                    
                    interface HistoryLocalRepository {
                        fun getAllHistory(pageSize: Int = 20): Flow<PagingData<HistoryEntity>>
                    }
                    """.trimIndent()
            )

            //https://github.com/zainulhassan815/newslayer
            SpanText(
                text = "Pagination 3.3.0",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    //Pagination with Search 
                    
                    @OptIn(ExperimentalCoroutinesApi::class)
                    class SearchViewModel(
                    ) : ViewModel() {

                        val searchFieldState: TextFieldState = TextFieldState()
                        private val searchQuery: Flow<String> = snapshotFlow {
                            searchFieldState.text.toString()
                        }
                        
                         private val _filters: MutableStateFlow<SearchScreenState> = MutableStateFlow(SearchScreenState())
    
                        val filters: StateFlow<SearchScreenState> = _filters
                        .onStart { fetchCatalogFilters() }
                        .stateIn(
                            scope = viewModelScope,
                            started = SharingStarted.Lazily,
                            initialValue = _filters.value
                        )
                        
                        private fun fetchCatalogFilters() {
                            viewModelScope.launch { }
                        }

                        @OptIn(FlowPreview::class)
                        val searchResult: Flow<PagingData<Release>> = combine(
                            searchQuery.debounce(500L),
                            filters.debounce(500L)
                        ) { searchQuery, filters ->
                            searchQuery to filters
                          }
                          .flatMapLatest { (searchQuery, filters) ->
           
                          }
                          .cachedIn(viewModelScope)
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun TopSearchInputField(
                        state: TextFieldState,
                        onSearch: (String) -> Unit,
                        modifier: Modifier = Modifier,
                        enabled: Boolean = true,
                        readOnly: Boolean = false,
                        textStyle: TextStyle = LocalTextStyle.current,
                        placeholder: @Composable (() -> Unit)? = null,
                        leadingIcon: @Composable (() -> Unit)? = null,
                        trailingIcon: @Composable (() -> Unit)? = null,
                        prefix: @Composable (() -> Unit)? = null,
                        suffix: @Composable (() -> Unit)? = null,
                        inputTransformation: InputTransformation? = null,
                        outputTransformation: OutputTransformation? = null,
                        scrollState: ScrollState = rememberScrollState(),
                        colors: TextFieldColors = inputFieldColors(),
                        windowInsets: WindowInsets = SearchBarDefaults.windowInsets,
                        interactionSource: MutableInteractionSource? = null
                    ) {
                        @Suppress("NAME_SHADOWING")
                        val interactionSource = interactionSource ?: remember { MutableInteractionSource() }

                        val focused = interactionSource.collectIsFocusedAsState().value
                        val focusRequester = remember { FocusRequester() }

                        val textColor =
                            textStyle.color.takeOrElse {
                                colors.textColor(enabled, isError = false, focused = focused)
                            }
                        val mergedTextStyle = textStyle.merge(TextStyle(color = textColor))

                        LaunchedEffect(Unit) { focusRequester.requestFocus() }

                        BasicTextField(
                            state = state,
                            modifier = modifier
                                .sizeIn(minHeight = InputFieldHeight)
                                .focusRequester(focusRequester)
                                .windowInsetsPadding(windowInsets),
                            enabled = enabled,
                            readOnly = readOnly,
                            lineLimits = TextFieldLineLimits.SingleLine,
                            textStyle = mergedTextStyle,
                            cursorBrush = SolidColor(colors.cursorColor(isError = false)),
                            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                            onKeyboardAction = { onSearch(state.text.toString()) },
                            interactionSource = interactionSource,
                            inputTransformation = inputTransformation,
                            outputTransformation = outputTransformation,
                            scrollState = scrollState,
                            decorator = TextFieldDefaults.decorator(
                                state = state,
                                enabled = enabled,
                                lineLimits = TextFieldLineLimits.SingleLine,
                                outputTransformation = outputTransformation,
                                interactionSource = interactionSource,
                                placeholder = placeholder,
                                leadingIcon =
                                leadingIcon?.let { leading ->
                                    { Box(Modifier.offset(x = SearchBarIconOffsetX)) { leading() } }
                                },
                                trailingIcon =
                                trailingIcon?.let { trailing ->
                                    { Box(Modifier.offset(x = -SearchBarIconOffsetX)) { trailing() } }
                                },
                                prefix = prefix,
                                suffix = suffix,
                                colors = colors,
                                contentPadding = TextFieldDefaults.contentPaddingWithoutLabel(),
                                container = {
                                    Box(modifier = Modifier.fillMaxSize())
                                }
                            )
                        )
                    }

                    private val SearchBarIconOffsetX: Dp = 4.dp
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    import androidx.paging.PagingSource
                    import androidx.paging.PagingState
                    import kotlin.math.max

                    internal class CommonPagingSource<T : Any>(
                        private val loadPage: suspend (page: Int, limit: Int) -> PaginatedResponse<T>,
                    ) : PagingSource<Int, T>() {
                        override suspend fun load(params: LoadParams<Int>): LoadResult<Int, T> {
                            val pageIndex = params.key ?: FIRST_PAGE_INDEX
                            val loadSize = params.loadSize
                            return try {
                                val page = loadPage(pageIndex + 1, loadSize)

                                val newCount = page.items.size
                                val total = page.total
                                val itemsBefore = pageIndex * NETWORK_PAGE_SIZE
                                val itemsAfter = max(total - (itemsBefore + newCount), 0)

                                val prevKey = if (pageIndex == FIRST_PAGE_INDEX) null else pageIndex - 1
                                val nextKey = if (itemsAfter == 0) null else pageIndex + (params.loadSize / NETWORK_PAGE_SIZE)

                                LoadResult.Page(
                                    data = page.items,
                                    prevKey = prevKey,
                                    nextKey = nextKey,
                                    itemsBefore = itemsBefore,
                                    itemsAfter = itemsAfter,
                                )
                            } catch (e: Exception) {
                                LoadResult.Error(e)
                            }
                        }

                        override fun getRefreshKey(state: PagingState<Int, T>): Int? {
                            val anchorPosition = state.anchorPosition ?: return null
                            val pageIndex = anchorPosition / NETWORK_PAGE_SIZE
                            return pageIndex
                        }

                        override val jumpingSupported: Boolean = true

                        data class PaginatedResponse<T>(
                            val items: List<T>,
                            val total: Int,
                        )

                        companion object {
                            const val NETWORK_PAGE_SIZE = 20
                            private const val FIRST_PAGE_INDEX = 0
                        }
                    }
                    
                    interface CatalogRepository {
                        fun getCatalogReleases(search: String? = null, filters: CatalogFilters? = null): PagingSource<Int, Release>
                    }
                    
                    internal class DefaultCatalogRepository(
                        private val catalogApi: CatalogApi
                    ) : CatalogRepository {
                        override fun getCatalogReleases(
                            search: String?,
                            filters: CatalogFilters?
                        ): PagingSource<Int, Release> {
                            return CommonPagingSource(
                                loadPage = { page, limit ->
                                    val result = catalogApi.getCatalogReleases(
                                        page = page,
                                        limit = limit,
                                        search = search,
                                        genres = filters?.genres?.map(Genre::id),
                                        types = filters?.types?.map(ReleaseType::toDto),
                                        seasons = filters?.seasons?.map(Season::toDto),
                                        fromYear = filters?.years?.start,
                                        toYear = filters?.years?.endInclusive,
                                        sorting = filters?.sortingTypes?.firstOrNull()?.toDto(),
                                        ageRatings = filters?.ageRatings?.map(AgeRating::toDto),
                                        publishStatuses = filters?.publishStatuses?.map(PublishStatus::toDto),
                                        productionStatuses = filters?.productionStatuses?.map(ProductionStatus::toDto),
                                    ).getOrElse { error ->
                                        throw IllegalStateException()
                                    }
                                    CommonPagingSource.PaginatedResponse(
                                        items = result.data.map(ReleaseDto::toDomain),
                                        total = result.meta.pagination.total
                                    )
                                }
                            )
                        }
                    }
                    
                    class GetCatalogReleasesPager(
                        private val catalogRepository: CatalogRepository
                    ) {
                        operator fun invoke(
                            search: String? = null,
                            filters: CatalogFilters? = null
                        ): Flow<PagingData<Release>> {
                            return Pager(
                                config = PagingConfig(
                                    pageSize = PAGE_SIZE,
                                    prefetchDistance = PAGE_SIZE,
                                    enablePlaceholders = true,
                                    initialLoadSize = PAGE_SIZE,
                                    jumpThreshold = PAGE_SIZE * 3,
                                ),
                                pagingSourceFactory = {
                                    catalogRepository.getCatalogReleases(
                                        search = search,
                                        filters = filters
                                    )
                                }
                            ).flow
                        }

                        companion object {
                            const val PAGE_SIZE = 20
                        }
                    }
                    
                    @Composable
                    internal fun ThreePaneScaffoldPaneScope.FeedPane(
                        items: LazyPagingItems<Release>,
                        onShowErrorMessage: (Throwable) -> Unit,
                    ) {
                        LaunchedEffect(items) {
                            snapshotFlow { items.loadState }.collect { loadState ->
                                (loadState.refresh as? LoadState.Error)?.let { onShowErrorMessage(it.error) }
                                (loadState.append as? LoadState.Error)?.let { onShowErrorMessage(it.error) }
                                (loadState.prepend as? LoadState.Error)?.let { onShowErrorMessage(it.error) }
                            }
                        }
                    }
                    
                    //viewmodel 
                        val releases: Flow<PagingData<Release>> = getCatalogReleasesPager()
                        .cachedIn(viewModelScope)
                        
                    inline fun <T : Any> FeedScope.pagingItems(
                        items: LazyPagingItems<T>, //LazyPagingItems<T>
                        noinline key: ((index: Int) -> Any)? = items.itemKey(),
                        crossinline itemContent: @Composable FeedItemScope.(index: Int, item: T?) -> Unit,
                    ) {
                        items(
                            count = items.itemCount,
                            key = key,
                            contentType = items.itemContentType { "Paging Items" },
                        ) {
                            itemContent(it, items[it])
                        }
                    }  
                    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import androidx.paging.PagingSource
                    import androidx.paging.PagingState
                    import kotlinx.coroutines.flow.first

                    class NewsArticlesPagingSource(
                        private val searchQuery: String? = null,
                        private val categories: Set<Category>? = null,
                        private val newsRepository: NewsRepository
                    ) : PagingSource<String, NewsArticle>() {

                        override fun getRefreshKey(state: PagingState<String, NewsArticle>): String? {
                            return state.anchorPosition?.let { anchor ->
                                state.closestPageToPosition(anchor)?.nextKey
                            }
                        }

                        override suspend fun load(
                            params: LoadParams<String>
                        ): LoadResult<String, NewsArticle> {
                            return newsRepository.getNewsArticles(
                                    query = NewsArticlesQuery(
                                        searchQuery = searchQuery,
                                        categories = categories,
                                        page = params.key,
                                    )
                                )
                                .first()
                                .fold(
                                    ifLeft = { failure ->
                                        val cause = when (failure) {
                                            is NewsRepositoryFailure.HttpError -> Throwable(failure.message)
                                            is NewsRepositoryFailure.NetworkError -> failure.cause
                                            is NewsRepositoryFailure.Unknown -> failure.cause
                                        }
                                        LoadResult.Error(cause)
                                    },
                                    ifRight = { resultPage ->
                                        LoadResult.Page(
                                            data = resultPage.newsArticles,
                                            nextKey = resultPage.nextPage,
                                            prevKey = resultPage.previousPage
                                        )
                                    }
                                )
                        }
                    }

                    data class Category(
                        val name: String
                    )

                    data class NewsArticlesPage(
                        val totalNewsArticles: Int,
                        val newsArticles: List<NewsArticle>,
                        val nextPage: String?,
                        val previousPage: String?,
                    )

                    data class NewsArticle(
                        val id: String,
                        val title: String,
                        val description: String,
                        val headerImageUrl: String?,
                        val source: NewsSource,
                        val publishDate: Instant,
                        val link: String,
                    )

                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewModelScope
                    import androidx.paging.Pager
                    import androidx.paging.PagingConfig
                    import androidx.paging.cachedIn
                    import dagger.assisted.Assisted
                    import dagger.assisted.AssistedFactory
                    import dagger.assisted.AssistedInject
                    import dagger.hilt.android.lifecycle.HiltViewModel

                    @HiltViewModel(assistedFactory = CategoryListViewModel.CategoryListViewModelFactory::class)
                    class CategoryListViewModel @AssistedInject constructor(
                        @Assisted categories: List<Category>,
                        newsRepository: NewsRepository,
                    ) : ViewModel() {

                        @AssistedFactory
                        interface CategoryListViewModelFactory {
                            fun create(categories: List<Category>): CategoryListViewModel
                        }

                        val pagingData = Pager(
                            config = PagingConfig(
                                pageSize = 10,
                                initialLoadSize = 10
                            ),
                            pagingSourceFactory = {
                                NewsArticlesPagingSource(
                                    categories = categories.toSet(),
                                    newsRepository = newsRepository
                                )
                            }
                        )
                            .flow
                            .cachedIn(viewModelScope)
                    }

                    @Composable
                    fun HomeScreenContent(
                        state: HomeScreenState.Success,
                        modifier: Modifier = Modifier
                    ) {
                        val forYouCategoryName = stringResource(R.string.feature_home_category_for_you)
                        val forYouCategory = remember { Category(forYouCategoryName) }

                        val categories = remember(state) {
                            with(state) { listOf(forYouCategory) + followedCategories + otherCategories }
                        }
                        val pagerState = rememberPagerState { categories.size }

                        Column(
                            modifier = modifier,
                        ) {
                            CategoryTabs(
                                modifier = Modifier.fillMaxWidth(),
                                categories = categories,
                                pagerState = pagerState
                            )

                            HorizontalPager(
                                state = pagerState,
                                key = { it },
                                beyondViewportPageCount = 1, // Load next page in advance
                            ) { page ->
                                CategoryList(
                                    viewModel = hiltViewModel(
                                        creationCallback = { factory: CategoryListViewModel.CategoryListViewModelFactory ->
                                            val selectedCategory = categories[page]
                                            val data = when {
                                                selectedCategory == forYouCategory -> state.followedCategories
                                                else -> listOf(selectedCategory)
                                            }
                                            factory.create(categories = data)
                                        },
                                        key = categories[page].name
                                    )
                                )
                            }
                        }
                    }

                    @Composable
                    fun CategoryList(
                        viewModel: CategoryListViewModel,
                        modifier: Modifier = Modifier
                    ) {
                        val pagingItems = viewModel.pagingData.collectAsLazyPagingItems()

                        val loading = pagingItems.loadState.refresh is LoadState.Loading
                        val hasError = pagingItems.loadState.refresh is LoadState.Error

                        when {
                            hasError -> ErrorMessage(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(MaterialTheme.spacing.large),
                                title = stringResource(R.string.feature_home_category_list_error_title),
                                subtitle = stringResource(R.string.feature_home_category_list_error_message),
                                icon = {
                                    Icon(
                                        painter = painterResource(CommonResources.drawable.common_resources_report),
                                        contentDescription = null
                                    )
                                },
                                action = {
                                    PrimaryButton(
                                        label = stringResource(R.string.feature_home_category_list_try_again_btn_label),
                                        onClick = {
                                            pagingItems.refresh()
                                        }
                                    )
                                }
                            )

                            loading -> Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center,
                            ) {
                                CircularProgressIndicator()
                            }

                            else -> ArticlesListScaffold(
                                modifier = modifier,
                                pagingItems = pagingItems,
                                onArticleClick = {}
                            )
                        }
                    }

                    @Singleton
                    class NewsRepositoryImpl @Inject constructor(
                        private val newsDataApi: NewsDataApi,
                    ) : NewsRepository {
                        override fun getNewsArticles(
                            query: NewsArticlesQuery
                        ): Flow<Either<NewsRepositoryFailure, NewsArticlesPage>> = flow {
                            newsDataApi.getNewsArticles(
                                searchQuery = query.searchQuery,
                                categories = query.categories?.joinToString(separator = ",") { it.name },
                                page = query.page
                            ).map {
                                it.toResultPage(previousPage = query.page)
                            }.mapLeft {
                                when (it) {
                                    is HttpError -> NewsRepositoryFailure.HttpError(it.code, it.message)
                                    is IOError -> NewsRepositoryFailure.NetworkError(it.cause)
                                    is UnexpectedCallError -> NewsRepositoryFailure.Unknown(it.cause)
                                }
                            }.let { emit(it) }
                        }
                    }

                    sealed interface NewsRepositoryFailure {
                        data class HttpError(
                            val code: Int,
                            val message: String
                        ) : NewsRepositoryFailure

                        data class NetworkError(
                            val cause: IOException
                        ) : NewsRepositoryFailure

                        data class Unknown(
                            val cause: Throwable
                        ) : NewsRepositoryFailure
                    }
                    """.trimIndent()
            )

            //https://github.com/vahitkeskin/Equatix.git (Dynamic GridView, Dialog left to right and right to left animation, Segment animation, Select item next position automatically move position, Game, Splash Animation, Notification (all platform))
            KotlinCode(
                code = """
                    //Select item automatically move position
                    
                    @Composable
                    fun LanguageSelectionCarousel(
                        currentLanguage: AppLanguage,
                        onLanguageSelected: (AppLanguage) -> Unit,
                        colors: EquatixDesignSystem.ThemeColors
                    ) {
                        val items = AppLanguage.entries
                        val infinitePageCount = Int.MAX_VALUE
                        val midPoint = infinitePageCount / 2
                        val startIndex = midPoint - (midPoint % items.size) + items.indexOf(currentLanguage)

                        val pagerState = rememberPagerState(
                            initialPage = startIndex,
                            pageCount = { infinitePageCount }
                        )

                        val scope = rememberCoroutineScope()

                        val selectedColor = Color(0xFF4CAF50) 
                        val unselectedIconColor = colors.textSecondary.copy(alpha = 0.6f) 

                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            // --- CAROUSEL ALANI ---
                            BoxWithConstraints(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(100.dp), 
                                contentAlignment = Alignment.Center
                            ) {
                                val cardWidth = maxWidth / 3.2f
                                val cardHeight = 72.dp
                                val padding = (maxWidth - cardWidth) / 2

                                HorizontalPager(
                                    state = pagerState,
                                    contentPadding = PaddingValues(horizontal = padding),
                                    pageSpacing = 16.dp,
                                    modifier = Modifier.fillMaxWidth()
                                ) { globalIndex ->
                                    val actualIndex = globalIndex % items.size
                                    val language = items[actualIndex]
                                    val isSelected = language == currentLanguage

                                    val appStrings by HomeViewModel().strings.collectAsState()
                                    val labelText = if (language == AppLanguage.SYSTEM) {
                                        appStrings.langSystem
                                    } else {
                                        language.label
                                    }

                                    val pageOffset = (
                                            (pagerState.currentPage - globalIndex) + pagerState.currentPageOffsetFraction
                                            ).absoluteValue

                                    val scale = lerp(0.85f, 1f, 1f - pageOffset.coerceIn(0f, 1f))
                                    val alpha = lerp(0.5f, 1f, 1f - pageOffset.coerceIn(0f, 1f))

                                    val backgroundColor by animateColorAsState(
                                        targetValue = if (isSelected) selectedColor.copy(alpha = 0.1f) else colors.cardBackground,
                                        label = "bgColor"
                                    )
                                    val borderColor = if (isSelected) selectedColor else colors.divider
                                    val contentColor by animateColorAsState(
                                        targetValue = if (isSelected) selectedColor else colors.textPrimary,
                                        label = "contentColor"
                                    )

                                    Box(
                                        modifier = Modifier
                                            .width(cardWidth)
                                            .height(cardHeight)
                                            .graphicsLayer {
                                                scaleX = scale
                                                scaleY = scale
                                                this.alpha = alpha
                                            },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Card(
                                            modifier = Modifier
                                                .fillMaxSize()
                                                .clip(RoundedCornerShape(16.dp))
                                                .border(
                                                    width = 1.dp,
                                                    color = borderColor,
                                                    shape = RoundedCornerShape(16.dp)
                                                )
                                                .clickable {
                                                    if (globalIndex != pagerState.currentPage) {
                                                        scope.launch { pagerState.animateScrollToPage(globalIndex) }
                                                    } else {
                                                        onLanguageSelected(language)
                                                    }
                                                },
                                            shape = RoundedCornerShape(16.dp),
                                            colors = CardDefaults.cardColors(containerColor = backgroundColor),
                                            elevation = CardDefaults.cardElevation(0.dp)
                                        ) {
                                            Box(
                                                modifier = Modifier
                                                    .fillMaxSize()
                                                    .padding(8.dp)
                                            ) {
                                                Column(
                                                    modifier = Modifier.align(Alignment.Center),
                                                    horizontalAlignment = Alignment.CenterHorizontally,
                                                    verticalArrangement = Arrangement.Center
                                                ) {
                                                    Text(
                                                        text = language.flagEmoji,
                                                        fontSize = 24.sp,
                                                        textAlign = TextAlign.Center
                                                    )
                                                    Spacer(modifier = Modifier.height(2.dp))
                                                    Text(
                                                        text = labelText,
                                                        style = MaterialTheme.typography.labelMedium.copy(
                                                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                                            letterSpacing = 0.5.sp
                                                        ),
                                                        color = contentColor,
                                                        maxLines = 1,
                                                        textAlign = TextAlign.Center
                                                    )
                                                }

                                                Icon(
                                                    imageVector = if (isSelected) Icons.Rounded.CheckCircle else Icons.Rounded.CheckBoxOutlineBlank,
                                                    contentDescription = if (isSelected) "Selected" else "Select",
                                                    tint = if (isSelected) selectedColor else unselectedIconColor,
                                                    modifier = Modifier
                                                        .size(20.dp)
                                                        .align(Alignment.TopEnd)
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/robert-muriithi/movies-catalogue-KMM (Horizontal Pagination)
//https://github.com/vahitkeskin/Equatix.git (Dynamic GridView, Dialog left to right and right to left animation, Segment animation, Select item next position automatically move position, Game, Splash Animation, Notification (all platform))
//https://github.com/longdt57/Kotlin-Multiplatform.git
//https://medium.com/@om.teastallstudio/jetpack-compose-paging-3-lazy-list-navigation-3-with-mvi-load-states-shimmer-placeholders-a4ced8224bf3
//https://www.droidunplugged.com/2026/01/cracking-android-sde2sde3-interviews-in_22.html
//https://medium.com/@ramadan123sayed/understanding-paging-3-in-jetpack-compose-from-pagingsource-to-offline-first-with-remotemediator-980a135b9008
//https://github.com/TejashreeDeshpande/InfiniteUi
//https://github.com/Shahidzbi4213/WallpaperApp-Cmp.git (Good Number pagination Desktop with AnimatedContent, Download, Set Wallpaper)